import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  TextField, 
  Button, 
  Stack, 
  useTheme,
  useMediaQuery
} from "@mui/material";
import { 
  Phone, 
  Email, 
  LocationOn, 
  AccessTime, 
  ArrowBack,
  Send,
  Shield,
  Handshake,
  Bolt
} from "@mui/icons-material";
import API_BASE_URL from "../Config/config";

const Contact = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({ 
    name: "", 
    mail: "", 
    phone: "", 
    subject: "", 
    message: "" 
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name || !formData.mail || !formData.message) {
      setStatusMessage("❌ Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contactus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            name: formData.name,
            mail: formData.mail,
            message: `[Phone: ${formData.phone || 'N/A'}] [Subject: ${formData.subject || 'N/A'}] ${formData.message}`
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatusMessage("✅ Message sent successfully! Our team will get back to you soon.");
        setFormData({ name: "", mail: "", phone: "", subject: "", message: "" });
      } else {
        setStatusMessage(`❌ ${data.error || "Failed to send message"}`);
      }
    } catch (err) {
      console.error("Contact form error:", err);
      setStatusMessage("❌ Server error, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    {
      icon: <Phone sx={{ fontSize: 28 }} />,
      title: "Call Support",
      value: "+91 62815 97586",
      footer: "Available Mon–Sat",
      subFooter: "9:00 AM – 6:00 PM",
      gradient: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
    },
    {
      icon: <Email sx={{ fontSize: 28 }} />,
      title: "Email Us",
      value: "connect@evolvexinnovations.org",
      footer: "Typical response",
      subFooter: "within 24 hours",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #2dd4bf 100%)",
    },
    {
      icon: <LocationOn sx={{ fontSize: 28 }} />,
      title: "Visit Us",
      value: "Bhoomatha Thalluri Heights,",
      value2: "Kankipadu, Vijayawada – 521151",
      footer: "Andhra Pradesh, India",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
    },
    {
      icon: <AccessTime sx={{ fontSize: 28 }} />,
      title: "Business Hours",
      value: "Monday – Saturday",
      value2: "9:00 AM – 6:00 PM",
      footer: "Sunday Closed",
      gradient: "linear-gradient(135deg, #10b981 0%, #3b82f6 100%)",
    },
  ];

  const features = [
    "Technical Support",
    "Platform Assistance",
    "College Partnerships",
    "Enterprise Solutions",
    "AI Interview Platform",
    "Product Feedback",
    "Campus Hiring Solutions",
  ];

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#F8FAFC", 
      pt: { xs: 4, md: 8 }, 
      pb: 10,
      fontFamily: "'Inter', sans-serif"
    }}>
      <Container maxWidth="xl" sx={{ maxWidth: "1600px !important" }}>
        {/* Back Button */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "50px",
              px: 3,
              py: 1,
              bgcolor: "white",
              color: "text.primary",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              "&:hover": { bgcolor: "#f1f5f9" },
              textTransform: "none",
              fontWeight: 600
            }}
          >
            Back
          </Button>
        </Box>

        {/* Top Section */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Box
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.5,
              borderRadius: "50px",
              bgcolor: "rgba(91, 33, 182, 0.1)",
              color: "#5B21B6",
              fontSize: "14px",
              fontWeight: 700,
              mb: 3,
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}
          >
            Contact Our Team
          </Box>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "32px", sm: "48px", md: "56px" },
              fontWeight: 800,
              color: "#111827",
              mb: 3,
              letterSpacing: "-0.02em"
            }}
          >
            Let's Build Your Future Together
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "18px",
              color: "#4B5563",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 500
            }}
          >
            Whether you have technical questions, partnership opportunities, institution onboarding queries, or product feedback, our team is here to help you every step of the way.
          </Typography>
        </Box>

        {/* Information Cards */}
        <Grid container spacing={4} sx={{ mb: 10, justifyContent: "center" }}>
          {infoCards.map((card, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  p: 4,
                  borderRadius: "20px",
                  border: "1px solid rgba(229, 231, 235, 0.5)",
                  bgcolor: "white",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 20px 40px rgba(91, 33, 182, 0.08)",
                    borderColor: "rgba(91, 33, 182, 0.2)",
                    "& .icon-circle": {
                        boxShadow: "0 0 20px rgba(91, 33, 182, 0.2)"
                    }
                  }
                }}
              >
                <Box
                  className="icon-circle"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: card.gradient,
                    color: "white",
                    mb: 4,
                    transition: "all 0.3s ease"
                  }}
                >
                  {React.cloneElement(card.icon, { sx: { fontSize: 32 } })}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: "#111827", fontSize: "20px" }}>
                  {card.title}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#4B5563", mb: 0.5, fontSize: "16px" }}>
                  {card.value}
                </Typography>
                {card.value2 && (
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#4B5563", mb: 2, fontSize: "16px" }}>
                    {card.value2}
                  </Typography>
                )}
                <Box sx={{ mt: "auto", pt: 2 }}>
                    <Typography variant="caption" sx={{ display: "block", color: "#9CA3AF", fontWeight: 500 }}>
                        {card.footer}
                    </Typography>
                    {card.subFooter && (
                        <Typography variant="caption" sx={{ display: "block", color: "#9CA3AF", fontWeight: 500 }}>
                            {card.subFooter}
                        </Typography>
                    )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main Content Section */}
        <Grid container spacing={3} alignItems="stretch" sx={{ mb: 10 }}>
          {/* Contact Form */}
          <Grid item xs={12} md={7} sx={{ display: "flex" }}>
            <Card
              sx={{
                width: "100%",
                p: { xs: 3, md: 4 },
                borderRadius: "24px",
                bgcolor: "white",
                border: "1px solid rgba(229, 231, 235, 0.5)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.04)"
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2.5, color: "#111827", fontSize: { xs: "18px", md: "20px" }, textAlign: "center" }}>
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit}>
                {/* Row 1: Full Name + Email */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, mb: 2.5 }}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "13px", color: "#374151" }}>Full Name</Typography>
                    <TextField
                      fullWidth
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F9FAFB",
                          "& fieldset": { borderColor: "rgba(229,231,235,1)" },
                          "&:hover fieldset": { borderColor: "#5B21B6" },
                          "&.Mui-focused fieldset": { borderColor: "#5B21B6", borderWidth: "2px" }
                        }
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "13px", color: "#374151" }}>Email Address</Typography>
                    <TextField
                      fullWidth
                      name="mail"
                      type="email"
                      placeholder="example@email.com"
                      value={formData.mail}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F9FAFB",
                          "& fieldset": { borderColor: "rgba(229,231,235,1)" },
                          "&:hover fieldset": { borderColor: "#5B21B6" },
                          "&.Mui-focused fieldset": { borderColor: "#5B21B6", borderWidth: "2px" }
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Row 2: Phone + Subject */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5, mb: 2.5 }}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "13px", color: "#374151" }}>Phone Number <span style={{ fontWeight: 400, color: "#9CA3AF" }}>(optional)</span></Typography>
                    <TextField
                      fullWidth
                      name="phone"
                      placeholder="+91 00000 00000"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F9FAFB",
                          "& fieldset": { borderColor: "rgba(229,231,235,1)" },
                          "&:hover fieldset": { borderColor: "#5B21B6" },
                          "&.Mui-focused fieldset": { borderColor: "#5B21B6", borderWidth: "2px" }
                        }
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "13px", color: "#374151" }}>Subject</Typography>
                    <TextField
                      fullWidth
                      name="subject"
                      placeholder="How can we help?"
                      value={formData.subject}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F9FAFB",
                          "& fieldset": { borderColor: "rgba(229,231,235,1)" },
                          "&:hover fieldset": { borderColor: "#5B21B6" },
                          "&.Mui-focused fieldset": { borderColor: "#5B21B6", borderWidth: "2px" }
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Row 3: Message — full width */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ mb: 1, fontWeight: 600, fontSize: "13px", color: "#374151" }}>Message</Typography>
                  <TextField
                    fullWidth
                    name="message"
                    multiline
                    rows={4}
                    placeholder="Write your message here..."
                    value={formData.message}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#F9FAFB",
                        "& fieldset": { borderColor: "rgba(229,231,235,1)" },
                        "&:hover fieldset": { borderColor: "#5B21B6" },
                        "&.Mui-focused fieldset": { borderColor: "#5B21B6", borderWidth: "2px" }
                      }
                    }}
                  />
                </Box>

                {/* Row 4: Submit button — full width */}
                <Button
                  fullWidth
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  endIcon={loading ? null : <Send />}
                  sx={{
                    height: "52px",
                    borderRadius: "12px",
                    background: "linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)",
                    fontSize: "15px",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "0 8px 20px rgba(124, 58, 237, 0.28)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: "linear-gradient(90deg, #4338CA 0%, #6D28D9 100%)",
                      transform: "translateY(-1px)",
                      boxShadow: "0 12px 28px rgba(124, 58, 237, 0.38)"
                    }
                  }}
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>

                {statusMessage && (
                  <Typography
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: "10px",
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: "13px",
                      bgcolor: statusMessage.startsWith("✅") ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
                      color: statusMessage.startsWith("✅") ? "#059669" : "#DC2626",
                      border: statusMessage.startsWith("✅") ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(239,68,68,0.2)"
                    }}
                  >
                    {statusMessage}
                  </Typography>
                )}
              </form>
            </Card>
          </Grid>

          {/* Side Content */}
          <Grid item xs={12} md={5} sx={{ display: "flex" }}>
            <Card
              sx={{
                width: "100%",
                p: { xs: 3, md: 4 },
                borderRadius: "24px",
                background: "linear-gradient(180deg, rgba(245, 243, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)",
                border: "1px solid rgba(229, 231, 235, 0.5)",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2.5, color: "#111827", fontSize: { xs: "18px", md: "20px" }, textAlign: "center" }}>
                Why Contact The Skill Genie?
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {features.map((feature, idx) => (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: "#7C3AED" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#4B5563", fontSize: "13px" }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Box sx={{
                mt: "auto",
                pt: 2.5,
                borderTop: "1px solid rgba(229, 231, 235, 0.8)",
                mb: 2.5
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#5B21B6",
                    lineHeight: 1.6,
                    fontStyle: "italic"
                  }}
                >
                  "Empowering students, colleges, and enterprises through AI-powered learning and career transformation."
                </Typography>
              </Box>

              <Stack spacing={1}>
                {[
                  { icon: <Bolt sx={{ fontSize: 16 }} />, label: "Fast Response" },
                  { icon: <Shield sx={{ fontSize: 16 }} />, label: "Secure Communication" },
                  { icon: <Handshake sx={{ fontSize: 16 }} />, label: "Dedicated Support" }
                ].map((badge, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 0.75,
                      borderRadius: "10px",
                      bgcolor: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      border: "1px solid rgba(229, 231, 235, 0.5)"
                    }}
                  >
                    <Box sx={{ color: "#7C3AED", display: "flex" }}>{badge.icon}</Box>
                    <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: "12px" }}>
                      {badge.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom CTA Section */}
        <Box
          sx={{
            borderRadius: "32px",
            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #A855F7 100%)",
            p: { xs: 6, md: 8 },
            textAlign: "center",
            color: "white",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Background shapes */}
          <Box sx={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.1)", blur: "40px" }} />
          <Box sx={{ position: "absolute", bottom: -100, left: -50, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.05)", blur: "60px" }} />
          
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "28px", md: "40px" }, color: "#FFFFFF" }}>
              Need Enterprise or Institution Solutions?
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: "800px", mx: "auto", mb: 5, fontSize: "18px", color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
              "Our team works with colleges, universities, enterprises, and training organizations to deliver AI-powered assessment, hiring, and learning platforms."
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate("/contact")}
                sx={{
                  bgcolor: "white",
                  color: "#5B21B6",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.8,
                  fontWeight: 700,
                  fontSize: "16px",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#f1f5f9", transform: "translateY(-2px)" },
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease"
                }}
              >
                Contact Sales Team
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/")}
                sx={{
                  borderColor: "rgba(255,255,255,0.5)",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.8,
                  fontWeight: 700,
                  fontSize: "16px",
                  textTransform: "none",
                  borderWidth: "2px",
                  "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)", transform: "translateY(-2px)", borderWidth: "2px" },
                  transition: "all 0.3s ease"
                }}
              >
                Explore The Skill Genie
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;



